﻿import React, { Component, ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import history from '../../shared/history';
import { AuthenticatedUser } from '../../shared/types';
import { Translate } from '../../shared/LanguageService';
import { Logger } from '../../telemetry/loggers/Logger';
import { useTelemetryContext } from '../../telemetry/telemetryContext';
import { getUserInfo } from '../../telemetry/utils';

type ExternalProps = {
    section: 'header' | 'content' | 'image_gallery';
    user: AuthenticatedUser;
};

type ErrorBoundaryLabels = {
    generalError__buttonLabel: string;
    generalError__errorDescription: string;
    headerError__buttonLabel: string;
    headerError__errorDescription: string;
};

type ErrorBoundaryProps = {
    logger: Logger;
    labels: ErrorBoundaryLabels;
} & ExternalProps;

type ErrorBoundaryState = {
    hasError: boolean;
};

type HeaderErrorBoundaryLabels = {
    button: string;
    description: string;
};

type HeaderErrorBoundaryProps = {
    labels: HeaderErrorBoundaryLabels;
    onReload: () => void;
};

function HeaderErrorBoundary({ labels, onReload }: HeaderErrorBoundaryProps) {
    function handleReload() {
        onReload();
    }

    return (
        <Box
            sx={{
                padding: '20px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Alert
                variant="filled"
                severity="error"
                sx={{ width: '406px', alignItems: 'center' }}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        sx={{ padding: '8px', fontSize: '13px' }}
                        onClick={handleReload}
                    >
                        {labels.button}
                    </Button>
                }
            >
                <Typography variant="body1" sx={{ fontSize: '14px' }}>
                    {labels.description}
                </Typography>
            </Alert>
        </Box>
    );
}

type GeneralErrorBoundaryLabels = {
    description: string;
    button: string;
};

type GeneralErrorBoundaryProps = {
    labels: GeneralErrorBoundaryLabels;
    onReload: () => void;
};

function GeneralErrorBoundary({ labels, onReload }: GeneralErrorBoundaryProps) {
    function handleReload() {
        onReload();
    }

    return (
        <Box
            sx={{
                padding: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <Box
                sx={{
                    borderRadius: '6px',
                    padding: '20px',
                    backgroundColor: '#d3d3d3',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    rowGap: '24px'
                }}
            >
                <Typography variant="h4" sx={{ display: 'flex', justifyContent: 'center' }}>
                    {labels.description}
                </Typography>
                <Button
                    variant="contained"
                    sx={(theme) => ({
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        width: '90px',
                        height: '30px',
                        padding: '20px 0'
                    })}
                    onClick={handleReload}
                >
                    {labels.button}
                </Button>
            </Box>
        </Box>
    );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private unsubscribeHistory = null;

    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        let { logger, section, user } = this.props;

        if (logger) {
            let additionalInfo = {
                section,
                errorInfo,
                userInfo: getUserInfo(user)
            };
            logger.logException(error, additionalInfo);
        }

        this.unsubscribeHistory = history.listen((location, action) => {
            this.setState({ hasError: false });
        });
    }

    componentWillUnmount() {
        if (this.unsubscribeHistory) {
            this.unsubscribeHistory();
        }
    }

    resetError = () => {
        this.setState({ hasError: false });
    };

    render() {
        let { hasError } = this.state;
        let { labels, section } = this.props;

        if (hasError && section === 'header') {
            return (
                <HeaderErrorBoundary
                    labels={{
                        button: labels.headerError__buttonLabel,
                        description: labels.headerError__errorDescription
                    }}
                    onReload={this.resetError}
                />
            );
        }

        if (hasError) {
            return (
                <GeneralErrorBoundary
                    labels={{
                        button: labels.generalError__buttonLabel,
                        description: labels.generalError__errorDescription
                    }}
                    onReload={this.resetError}
                />
            );
        }

        return this.props.children;
    }
}

function TranslatedErrorBoundary({
    children,
    ...props
}: ExternalProps & { logger: Logger } & { children: ReactNode }) {
    let labels: ErrorBoundaryLabels = {
        generalError__buttonLabel: Translate('500-error/button'),
        generalError__errorDescription: Translate('500-error/description'),
        headerError__buttonLabel: Translate('500-error/header/button'),
        headerError__errorDescription: Translate('500-error/header/description')
    };

    return (
        <ErrorBoundary {...props} labels={labels}>
            {children}
        </ErrorBoundary>
    );
}

function ErrorBoundaryWithLogger({
    children,
    section,
    user
}: ExternalProps & { children: ReactNode }) {
    let { logger } = useTelemetryContext();
    return (
        <TranslatedErrorBoundary section={section} logger={logger} user={user}>
            {children}
        </TranslatedErrorBoundary>
    );
}

export default ErrorBoundaryWithLogger;
